"use server";

import { noteLogin } from "@/lib/analytics/auth-event";
import { redirect } from "next/navigation";
import { gql } from "@/lib/lecturehub/client";
import { setTokenCookie, clearTokenCookie } from "@/lib/lecturehub/auth";
import { friendlyMessage } from "@/lib/lecturehub/errors";
import { getRequestDict } from "@/lib/i18n-server";
import { Q_LOGIN } from "@/lib/lecturehub/operations";
import { safeRedirect } from "@/lib/routes/canonicalize";
import { defaultLang, isLang, langPath } from "@/lib/i18n";
import type { FormState } from "@/lib/actions/result";
import { clearSignupReturn } from "@/lib/signup-return";

export type LoginState = FormState;

/** The form's hidden `lang` is rendered from the validated route param, but a
 *  POST body is whatever arrives — and it feeds `langPath`, the fallback that
 *  `safeRedirect` does NOT check. Validate the shape instead of casting. */
function langField(formData: FormData) {
  const raw = String(formData.get("lang") ?? "");
  return isLang(raw) ? raw : defaultLang();
}

export async function loginAction(_prev: LoginState, formData: FormData): Promise<LoginState> {
  const email = String(formData.get("email") ?? "").trim();
  const password = String(formData.get("password") ?? "");
  const lang = langField(formData);
  const next = safeRedirect(String(formData.get("redirect") ?? ""), langPath(lang, "/dashboard"));

  const t = (await getRequestDict()).errors;
  if (!email || !password) {
    return { ok: false, error: t.emailPasswordRequired };
  }

  let token: string;
  try {
    const data = await gql<{ login: { token: string } }>(
      Q_LOGIN,
      { email, password },
      { asGuest: true, cache: "no-store" },
    );
    token = data.login.token;
  } catch (err) {
    return { ok: false, error: friendlyMessage(err, t) };
  }

  await setTokenCookie(token);
  await clearSignupReturn();
  await noteLogin("email");
  redirect(next);
}

export async function logoutAction(formData: FormData) {
  const lang = langField(formData);
  await clearTokenCookie();
  await clearSignupReturn();
  redirect(langPath(lang, "/"));
}
